import { WIDGETS, SIZE_LABELS } from "@/lib/widgets";
import { WidgetContent } from "./WidgetContent";
import {
  GripVertical, ChevronDown, ChevronUp, Settings2, EyeOff, Maximize2, MoreVertical,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export function WidgetCard({ instanceId, instance, editing, onCollapse, onHide, onResize, onConfigure }) {
  const widget = WIDGETS[instance.type];
  if (!widget) return null;
  const Icon = widget.icon;
  const collapsed = instance.collapsed;

  return (
    <div className={cn("flex h-full flex-col overflow-hidden rounded-xl border bg-card shadow-soft",
      editing ? "border-dashed border-accent/60" : "border-border")}
      data-testid={`widget-${instance.type}`}>
      <div className="flex items-center gap-2 border-b border-border px-3.5 py-2.5">
        {editing && (
          <span className="widget-drag-handle cursor-grab text-muted-foreground active:cursor-grabbing" data-testid={`drag-${instance.type}`}>
            <GripVertical className="h-4 w-4" />
          </span>
        )}
        <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg",
          widget.accent ? "bg-accent-subtle text-accent" : "bg-secondary text-primary")}>
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="min-w-0 flex-1 truncate font-heading text-base font-semibold text-foreground">{widget.title}</h3>

        <button onClick={onCollapse} className="text-muted-foreground hover:text-foreground" data-testid={`collapse-${instance.type}`}
          title={collapsed ? "Expand" : "Collapse"}>
          {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>

        {editing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-muted-foreground hover:text-foreground" data-testid={`menu-${instance.type}`}>
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {widget.config?.length > 0 && (
                <DropdownMenuItem onClick={onConfigure} data-testid={`configure-${instance.type}`}>
                  <Settings2 className="mr-2 h-4 w-4" /> Configure
                </DropdownMenuItem>
              )}
              <DropdownMenuLabel className="pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">Size</DropdownMenuLabel>
              {Object.entries(SIZE_LABELS).map(([key, label]) => (
                <DropdownMenuItem key={key} onClick={() => onResize(key)} data-testid={`size-${instance.type}-${key}`}
                  className={cn(instance.size === key && "text-accent")}>
                  <Maximize2 className="mr-2 h-4 w-4" /> {label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onHide} className="text-destructive" data-testid={`hide-${instance.type}`}>
                <EyeOff className="mr-2 h-4 w-4" /> Hide widget
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {!collapsed && (
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin px-3.5 py-3">
          <WidgetContent widget={widget} instance={instance} />
        </div>
      )}
    </div>
  );
}
